/**
 * DeliveryTermsPage — CRUD cho bảng delivery_terms (Sprint 5).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm, Controller } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListDeliveryTermsCrudQuery,
  useCreateDeliveryTermMutation,
  useUpdateDeliveryTermMutation,
  useDeleteDeliveryTermMutation,
} from '@/store/api/masterDataCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { DeliveryTerm } from '@/types/api'

interface DTFormValues {
  delivery_term: string
  description: string
  is_bonded: boolean
}

const EMPTY: DTFormValues = { delivery_term: '', description: '', is_bonded: false }

export function DeliveryTermsPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<DeliveryTerm | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<DeliveryTerm | null>(null)

  const { data, isLoading, error, isFetching } = useListDeliveryTermsCrudQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createDeliveryTerm, { isLoading: creating }] = useCreateDeliveryTermMutation()
  const [updateDeliveryTerm, { isLoading: updating }] = useUpdateDeliveryTermMutation()
  const [deleteDeliveryTerm, { isLoading: deleting }] = useDeleteDeliveryTermMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset, control } = useForm<DTFormValues>({ defaultValues: EMPTY })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        delivery_term: editing.delivery_term,
        description: editing.description ?? '',
        is_bonded: false,
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.delivery_term, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'delivery_term', headerName: 'Delivery Term', width: 160 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 240,
        valueFormatter: (value: unknown) => {
          const v = value as string | null | undefined
          return typeof v === 'string' && v.length > 60 ? v.slice(0, 60) + '…' : (v ?? '')
        },
      },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as DeliveryTerm) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as DeliveryTerm) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: DeliveryTerm = {
      delivery_term: values.delivery_term.trim(),
      description: values.description || null,
    }
    try {
      if (isEdit && editing) {
        await updateDeliveryTerm({ id: editing.delivery_term, body: payload }).unwrap()
        snack.success(`Updated "${editing.delivery_term}"`)
      } else {
        await createDeliveryTerm(payload).unwrap()
        snack.success(`Created "${payload.delivery_term}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteDeliveryTerm(confirmDelete.delivery_term).unwrap()
      snack.success(`Deleted "${confirmDelete.delivery_term}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Delivery Terms</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          New Delivery Term
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Search"
          placeholder="Term, description..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as DeliveryTerm)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit Delivery Term' : 'New Delivery Term'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Delivery Term *" size="small" fullWidth {...register('delivery_term')} disabled={isEdit} />
          <TextField label="Description" size="small" fullWidth multiline rows={3} {...register('description')} />
          <Controller
            name="is_bonded"
            control={control}
            render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Is Bonded" />
            )}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Delivery Term"
        message={confirmDelete ? `Are you sure you want to delete "${confirmDelete.delivery_term}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default DeliveryTermsPage
